import React, { useContext, useEffect, useState } from 'react';
import { Button, Row, Card, Image, Tooltip, Typography, Col, Modal, Pagination } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  DragOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getMenuId } from '../../utils/helpers';
import { useBrands } from '../../hooks/useMenuQuery';
import DndList from '../../components/DndList/DndList';

const { Meta } = Card;
const { Title, Text } = Typography;

function Brands({ history }) {
  const navigate = useNavigate();
  const { setHeaderComponent, isMobile } = useContext(LayoutContext);
  const [selectedItemDelete, setSelectedItemToDelete] = useState();
  const [sortMode, setSortMode] = useState(false);
  const [tempSortedList, setTempSortedList] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // Show 20 brands per page

  const { doFetch: fetchDelete } = useFetch();
  const { doFetch: fetchSort } = useFetch();
  const menuId = getMenuId();

  // Fetch brands with pagination
  const { data: brandsData, isLoading: isLoadingBrands, isFetching: isFetchingBrands, error: brandsError, refetch: refetchBrands } = useBrands(menuId, currentPage, pageSize);

  useEffect(() => {
    setHeaderComponent('Brand List');

    return () => {
      setHeaderComponent(null);
    };
  }, []);

  const handleItemDelete = () => {
    fetchDelete({
      url: URLS.deleteBrand({
        brandId: selectedItemDelete.id,
      }),
      method: 'DELETE',
      onSuccess: () => {
        refetchBrands();
        setSelectedItemToDelete();
      },
    });
  };

  const handleSortChange = newList => {
    setTempSortedList(newList);
    setHasUnsavedChanges(true);
  };

  const handleSaveSortOrder = () => {
    fetchSort({
      url: URLS.brandPrioritiesUpdate,
      method: 'POST',
      params: {
        priorities: tempSortedList.map(brand => brand.id),
      },
      onSuccess: () => {
        setSortMode(false);
        setHasUnsavedChanges(false);
        setTempSortedList([]);
        refetchBrands();
      },
    });
  };

  const handleCancelSort = () => {
    setSortMode(false);
    setTempSortedList([]);
    setHasUnsavedChanges(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSortMode(false); // Exit sort mode when changing pages
    setTempSortedList([]);
    setHasUnsavedChanges(false);
  };

  const enterSortMode = () => {
    setSortMode(true);
    // Transform brands to include translations property, just like in non-sort mode
    const transformedBrands = (brandsData?.brands || []).map(brand => ({
      ...brand,
      translations: brand.brand_translations?.map(translation => ({
        lanId: translation.language_id,
        lanName: translation.language_id === 1 ? 'العربية' :
                translation.language_id === 2 ? 'English' : 'کوردی',
        name: translation.name_text
      })) || [],
      image: brand.image_url
    }));
    setTempSortedList(transformedBrands);
    setHasUnsavedChanges(false);
  };

  // Show loading state
  if (isLoadingBrands) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading brands...</div>
      </div>
    );
  }

  // Show error state
  if (brandsError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        <div>Error loading brands: {brandsError.message}</div>
      </div>
    );
  }

  return (
    <div className="brands-page-container">
      {/* Material Design Linear Progress Bar */}
      {isFetchingBrands && !isLoadingBrands && (
        <>
          <style>{`
            @keyframes indeterminate {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(400%);
              }
            }
            @keyframes indeterminate-short {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(300%);
              }
            }
          `}</style>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            zIndex: 9999,
            backgroundColor: '#e0e0e0',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              height: '100%',
              width: '25%',
              backgroundColor: '#1890ff',
              animation: 'indeterminate 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite',
            }} />
            <div style={{
              position: 'absolute',
              height: '100%',
              width: '25%',
              backgroundColor: '#1890ff',
              animation: 'indeterminate-short 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite',
              animationDelay: '1.15s',
            }} />
          </div>
        </>
      )}

      {/* Header Section */}
      <div className="brands-header-section">
        <Row
          className="brands-header-row"
          justify="space-between"
          align="middle"
          gutter={[16, 16]}
        >
          <Col xs={24} sm={12} md={14}>
            <Title level={4} className="brands-title">
              Available Brands: ({brandsData?.total || 0})
            </Title>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <Row gutter={[8, 8]} justify={isMobile ? 'center' : 'end'}>
              <Col xs={24} sm={sortMode ? 8 : 12} md={sortMode ? 6 : 8}>
                <Button
                  type="primary"
                  icon={<FolderAddOutlined />}
                  onClick={() => navigate('/brand-create')}
                  className="brands-action-btn"
                  disabled={sortMode}
                >
                  {isMobile ? 'Add' : 'Add New Brand'}
                </Button>
              </Col>
              {sortMode ? (
                <>
                  <Col xs={12} sm={8} md={6}>
                    <Button
                      type="dashed"
                      icon={<LeftOutlined />}
                      onClick={handleCancelSort}
                      className="brands-action-btn"
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Button
                      type="primary"
                      onClick={handleSaveSortOrder}
                      className="brands-action-btn"
                      disabled={!hasUnsavedChanges}
                    >
                      Save
                    </Button>
                  </Col>
                </>
              ) : (
                <Col xs={24} sm={12} md={8}>
                  <Button
                    type="primary"
                    icon={<DragOutlined />}
                    onClick={enterSortMode}
                    className="brands-action-btn"
                  >
                    Sort
                  </Button>
                </Col>
              )}
            </Row>
          </Col>
        </Row>

        {/* Sort Mode Info */}
        {sortMode && (
          <div className="sort-mode-info">
            <Text type="secondary">
              🔄 Drag and drop brands to reorder. {hasUnsavedChanges && "Don't forget to save your changes!"}
            </Text>
          </div>
        )}
      </div>

      {/* Brands Grid */}
      <div className="brands-content-section">
        <Row
          gutter={[16, 16]}
          className={`brands-grid ${sortMode ? 'sort-mode' : ''}`}
        >
          <DndList
            list={sortMode ? tempSortedList : (brandsData?.brands?.map(brand => ({
              ...brand,
              translations: brand.brand_translations?.map(translation => ({
                lanId: translation.language_id,
                lanName: translation.language_id === 1 ? 'العربية' :
                        translation.language_id === 2 ? 'English' : 'کوردی',
                name: translation.name_text
              })) || [],
              image: brand.image_url
            })) || [])}
            onChange={handleSortChange}
            sortMode={sortMode}
            renderItem={brand => {
              const colProps = sortMode
                ? {
                  xs: 24,
                  sm: 12,
                  md: 8,
                  lg: 6,
                  style: { minWidth: isMobile ? '100%' : '220px' }
                }
                : {
                  xs: 24,
                  sm: 12,
                  md: 8,
                  lg: 6,
                  xl: 4
                };

              return (
                <Col {...colProps} key={brand.id}>
                  <Card
                    className={`brand-card ${sortMode ? 'dragging' : ''}`}
                    cover={
                      <div className="brand-image-wrapper">
                        <img
                          alt={`${brand.translations[0]?.name || 'brand'} image`}
                          className="brand-card-image"
                          src={brand.image}
                          onError={(e) => {
                            const fallbackUrl = getImageFallbackUrl(e.target.src);
                            if (fallbackUrl !== e.target.src) {
                              e.target.src = fallbackUrl;
                            } else {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div className="brand-image-placeholder" style={{ display: 'none' }}>
                          <span>No Image</span>
                        </div>
                      </div>
                    }
                    actions={sortMode ? [] : [
                      <Tooltip placement="top" title="Delete">
                        <DeleteOutlined
                          type="delete"
                          key="delete"
                          onClick={() => setSelectedItemToDelete(brand)}
                          className="brand-action-icon"
                        />
                      </Tooltip>,
                      <Tooltip placement="top" title="Update">
                        <EditOutlined
                          type="edit"
                          key="edit"
                          onClick={() => navigate(`/brand/${brand.id}`)}
                          className="brand-action-icon"
                        />
                      </Tooltip>,
                    ]}
                  >
                    <Meta
                      title={
                        <div className="brand-translations">
                          {brand.translations.map((translation, index) => (
                            <div key={index} className="translation-row">
                              <Text className="translation-text">
                                <span className="language-label">{translation.lanName}:</span>
                                <span className="translation-name">{translation.name}</span>
                              </Text>
                            </div>
                          ))}
                        </div>
                      }
                      description={
                        !sortMode && brand.productsCount !== undefined && (
                          <Text type="secondary" className="products-count">
                            {brand.productsCount} products
                          </Text>
                        )
                      }
                    />
                    {sortMode && (
                      <div className="drag-indicator">
                        <DragOutlined />
                        <Text type="secondary">Drag to reorder</Text>
                      </div>
                    )}
                  </Card>
                </Col>
              );
            }}
          />
        </Row>
      </div>

      {/* Brands Pagination */}
      {brandsData && brandsData.pages > 1 && (
        <div style={{ textAlign: 'center', marginTop: '24px', marginBottom: '24px' }}>
          <Pagination
            current={currentPage}
            total={brandsData.total || 0}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} brands`}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete brand"
        open={!!selectedItemDelete?.id}
        onOk={handleItemDelete}
        onCancel={() => setSelectedItemToDelete()}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          This brand has <strong>{selectedItemDelete?.productsCount}</strong> product(s).
          Do you confirm deleting the brand with all its products?
        </p>
        <Text type="warning">⚠️ This action cannot be undone.</Text>
      </Modal>
    </div>
  );
}

export default Brands;
